import { inject, Injectable } from '@angular/core';
import { firstValueFrom, from, map, Observable, of, switchMap } from 'rxjs';
import { Request, RequestStats } from '../models/request.model';
import { FirestoreService } from './firestore.service';
import { CollectionTables } from '../models/CollectionTables.model';
import { CreateRequestModel } from '../models/create-request.model';
import { AuthService } from './auth.service';
import { BusinessRegistrationService } from './business-registration.service';
import {
  formatFirestoreDateTime,
  firestoreTimestampNow,
  normalizeRequestTimeFieldsForWrite,
  timestampToMillis,
} from '../utils/firestore-timestamp.util';

@Injectable({
  providedIn: 'root',
})
export class RequestService {
  private firestoreService = inject(FirestoreService);
  private authService = inject(AuthService);
  private businessRegistrationService = inject(BusinessRegistrationService);

  constructor() {}

  getAllRequests(
    adminId: string,
    filters?: { type?: string; propertyId?: string; excludeType?: string; excludeSubtype?: string }
  ): Observable<Request[]> {
    return this.firestoreService
      .queryDocuments(CollectionTables.requests, [
        { field: 'managementBusinessId', operator: '==', value: adminId },
      ])
      .pipe(
        map((docs: any[]) => {
          let transformed = docs.map((doc) => this.transformFirebaseRequest(doc));
          if (filters?.type) {
            transformed = transformed.filter((r) => (r.type || '') === filters.type);
          }
          if (filters?.excludeType) {
            transformed = transformed.filter((r) => (r.type || '') !== filters.excludeType);
          }
          if (filters?.propertyId) {
            transformed = transformed.filter((r) => (r.propertyId || '') === filters.propertyId);
          }
          if (filters?.excludeSubtype) {
            transformed = transformed.filter(
              (r) => (r.subtype || '').trim() !== filters.excludeSubtype
            );
          }
          return transformed;
        })
      );
  }

  getRequestsByStatus(adminId: string, status: string): Observable<Request[]> {
    if (status === 'all') {
      return this.getAllRequests(adminId);
    }

    return this.firestoreService
      .queryDocuments(CollectionTables.requests, [
        { field: 'managementBusinessId', operator: '==', value: adminId },
        { field: 'status', operator: '==', value: status },
      ])
      .pipe(
        map((docs: any[]) => {
          return docs.map((doc) => this.transformFirebaseRequest(doc));
        })
      );
  }

  getRequestsByDate(
    adminId: string,
    date: Date,
    filters?: { type?: string; propertyId?: string; excludeType?: string; excludeSubtype?: string }
  ): Observable<Request[]> {
    // Get start and end of day timestamps
    const startOfDay = new Date(date);
    startOfDay.setHours(0, 0, 0, 0);

    const endOfDay = new Date(date);
    endOfDay.setHours(23, 59, 59, 999);

    const startTimestamp = startOfDay.getTime();
    const endTimestamp = endOfDay.getTime();

    console.log('Filtering by date:', date.toDateString());
    console.log('Start timestamp:', startTimestamp, new Date(startTimestamp));
    console.log('End timestamp:', endTimestamp, new Date(endTimestamp));

    // Fetch all requests and filter client-side
    // (Firestore doesn't support multiple range queries with equality filter)
    return this.getAllRequests(adminId, filters).pipe(
      map((requests) => {
        const filtered = requests.filter((request) => {
          if (!request.createdOn) return false;

          const requestTimestamp = timestampToMillis(request.createdOn);
          if (requestTimestamp == null) return false;

          const isInRange =
            requestTimestamp >= startTimestamp && requestTimestamp <= endTimestamp;
          
          if (isInRange) {
            console.log('Request matches date:', request.id, new Date(requestTimestamp));
          }
          
          return isInRange;
        });
        
        console.log(`Found ${filtered.length} requests for date ${date.toDateString()}`);
        return filtered;
      })
    );
  }

  getById(id: string): Observable<Request | undefined> {
    return this.getRequestById(id);
  }

  /**
   * Creates a new request in Firebase with all collected data from the 5-step flow
   * @param requestData Partial request data from the wizard steps
   * @returns Observable with the created document ID or null if failed
   */
  createRequest(requestData: Partial<CreateRequestModel>): Observable<string | null> {
    const currentUserProfile = this.authService.getUserProfile();
    const currentAuthUser = this.authService.getCurrentUser();

    if (!currentUserProfile || !currentAuthUser) {
      console.error('Cannot create request: User not authenticated');
      return of(null);
    }

    const authUid = currentAuthUser.uid;

    return from(this.buildPartyFields(authUid, currentUserProfile, requestData)).pipe(
      switchMap((party) => {
        if (!party) {
          console.error('Cannot create request: No management business found for user');
          return of(null);
        }

        const fullRequest: CreateRequestModel = {
          ...requestData,
          ...party,
          createdBy: 'com.servvis.concierge.web',
          createdOn: firestoreTimestampNow(),
          status: requestData.status || 'new',
          version: 1,
          quoteAccepted: false,
          includeTax: true,
        };

        normalizeRequestTimeFieldsForWrite(fullRequest as Record<string, unknown>);

        // Removed legacy fields (replaced by client/provider/management parties)
        delete (fullRequest as Record<string, unknown>)['vendorId'];
        delete (fullRequest as Record<string, unknown>)['vendorName'];
        delete (fullRequest as Record<string, unknown>)['adminId'];
        delete (fullRequest as Record<string, unknown>)['adminName'];
        delete (fullRequest as Record<string, unknown>)['administratorId'];
        delete (fullRequest as Record<string, unknown>)['businessId'];
        delete (fullRequest as Record<string, unknown>)['businessName'];

        console.log('Creating request with data:', fullRequest);

        return this.firestoreService.addDocumentWithId(CollectionTables.requests, fullRequest);
      })
    );
  }

  /**
   * Resolves the client / management / provider party fields for a request or task.
   *
   * - `clientId`/`clientName`: the signed-in user (the one creating the document).
   * - `managementId`/`managementName`: same as the client (a management user creates it).
   * - `managementBusinessId`/`managementBusinessName`: the user's management business.
   * - `providerId`/`providerName`: a provider business employee (requests only).
   * - `providerBusinessId`/`providerBusinessName`: the provider business (requests only).
   *
   * Provider fields are only resolved when `providerBusinessId` is supplied
   * (i.e. for service requests, not internal tasks).
   *
   * @returns the party fields, or `null` when no management business is found.
   */
  private async buildPartyFields(
    authUid: string,
    profile: { id?: string; firstName?: string; lastName?: string },
    requestData: Partial<CreateRequestModel>
  ): Promise<Partial<CreateRequestModel> | null> {
    const clientId = this.authService.getCachedUserDocumentId() ?? profile.id ?? '';
    const clientName = `${profile.firstName ?? ''} ${profile.lastName ?? ''}`.trim();

    const managementBusinessId = await this.authService.getAdministratorId(authUid);
    if (!managementBusinessId) {
      return null;
    }
    const managementBusiness = await this.businessRegistrationService.getBusinessInfo(
      managementBusinessId
    );
    const managementBusinessName = managementBusiness?.name || '';

    const party: Partial<CreateRequestModel> = {
      clientId,
      clientName,
      managementId: clientId,
      managementName: clientName,
      managementBusinessId,
      managementBusinessName,
    };

    // Provider parties — only for service requests (a provider business is selected).
    const providerBusinessId = (requestData.providerBusinessId || '').trim();
    if (providerBusinessId) {
      const providerBusiness = await this.businessRegistrationService.getBusinessInfo(
        providerBusinessId
      );
      const providerBusinessName =
        requestData.providerBusinessName || providerBusiness?.name || '';

      party.providerBusinessId = providerBusinessId;
      party.providerBusinessName = providerBusinessName;

      const representative = await this.getBusinessRepresentative(providerBusinessId);
      party.providerId = representative?.userId || '';
      party.providerName = representative?.name || '';
    }

    return party;
  }

  /**
   * Returns a representative employee (userId + name) of a business, preferring the
   * owner/manager/admin, otherwise the first employee. Used to populate providerId/providerName
   * from businesses/{businessId}/employees.
   */
  private async getBusinessRepresentative(
    businessId: string
  ): Promise<{ userId: string; name: string } | null> {
    try {
      const employees = await firstValueFrom(
        this.firestoreService.getCollection<any>(
          `${CollectionTables.businesses}/${businessId}/${CollectionTables.employees}`
        )
      );
      if (!employees || employees.length === 0) {
        return null;
      }
      const preferredRoles = ['owner', 'manager', 'admin'];
      const preferred =
        employees.find((e: any) => preferredRoles.includes((e?.role || '').toLowerCase())) ||
        employees[0];
      return {
        userId: preferred?.userId || preferred?.id || '',
        name: preferred?.name || '',
      };
    } catch (error) {
      console.warn('Could not resolve provider representative for business', businessId, error);
      return null;
    }
  }

  // create(request: Omit<Request, 'id' | 'createdDate' | 'updatedDate'>): Observable<Request> {
  //   const newRequest: Request = {
  //     ...request,
  //     id: this.getNextId(),
  //     createdDate: new Date(),
  //     updatedDate: new Date(),
  //   };
  //   this.requests.push(newRequest);
  //   return of(newRequest);
  // }

  private transformFirebaseRequest(doc: any): Request {
    const data = doc.data || doc;
    return {
      ...data,
      // Transform Firebase data to match UI expectations
      id: data.id || doc.id,
      requestNumber: data.id ? `#${data.id.substring(0, 7)}` : '#0000000',
      dateCreated: data.createdOn
        ? formatFirestoreDateTime(data.createdOn)
        : 'Unknown Date',
      providerName: data.providerBusinessName || 'Unknown Provider',
      providerLogo: data.providerLogo || '', // Use empty string to show initials placeholder
      agent: data.agentName || data.providerName || 'Unassigned',
      unreadMessages: 0, // Default value
      status: data.status || 'pending',
    };
  }

  getRequestById(id: string): Observable<Request | undefined> {
    return this.firestoreService.getDocument(CollectionTables.requests, id).pipe(
      map((doc: any) => {
        if (doc) {
          return this.transformFirebaseRequest(doc);
        }
        return undefined;
      })
    );
  }

  /**
   * Partial update for an existing request document (e.g. editing an internal task).
   */
  updateRequest(
    id: string,
    data: Partial<CreateRequestModel> & Record<string, unknown>
  ): Observable<boolean> {
    return this.firestoreService.updateDocumentRaw(CollectionTables.requests, id, data);
  }

  /**
   * Counters for the Home page cards, scoped like the Requests page (UAE-448): service
   * requests only — tasks and marketplace requests are excluded — and limited to the
   * selected property when one is passed. Without the same scope the Home page counted
   * requests the Requests page never shows.
   */
  getRequestStats(adminId: string, propertyId?: string): Observable<RequestStats> {
    return this.getAllRequests(adminId, {
      excludeType: 'task',
      excludeSubtype: 'marketplace',
      ...(propertyId ? { propertyId } : {}),
    }).pipe(
      map((requests: Request[]) => {
        // Current: Active requests that are not yet completed
        const currentStatuses = ['new', 'estimated', 'confirmed', 'draft'];
        // New: Only requests with status "new" (need vendor assignment)
        const newCount = requests.filter((r) => r.status === 'new').length;
        // Finished: Completed or closed requests
        const finishedStatuses = ['completed', 'cancelled', 'closed'];
        
        const stats: RequestStats = {
          total: requests.length,
          current: requests.filter((r) => currentStatuses.includes(r.status || '')).length,
          new: newCount,
          finished: requests.filter((r) => finishedStatuses.includes(r.status || '')).length,
        };
        
        console.log('📊 Request stats:', stats);
        return stats;
      })
    );
  }
}
